import classNames from 'classnames';
import { forwardRef, useEffect, useState } from 'react';

type SwitchProps = {
  defaultChecked?: boolean;
  disabled?: boolean;
  onChange?: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (_event: React.MouseEvent<HTMLInputElement>) => void;
  borderColor?: string;
  bgColor?: string;
};

const Switch = forwardRef<HTMLInputElement, SwitchProps>((props, ref) => {
  const {
    defaultChecked = false,
    disabled = false,
    onChange,
    onClick,
    borderColor = 'border-blue-400', // default border color
    bgColor = 'bg-blue-400', // default background color
  } = props;

  const [checked, setChecked] = useState(defaultChecked);

  useEffect(() => {
    setChecked(defaultChecked);
  }, [defaultChecked]);

  const switchRoot = classNames('inline-flex align-middle w-10 h-6 rounded-3xl relative border-2', {
    [borderColor]: checked, // dynamic border color
    'border-gray-300': !checked, // default when unchecked
    'opacity-60': disabled,
  });

  const inputStyles = classNames('z-10 opacity-0 w-full h-full', {
    [bgColor]: checked, // dynamic background color
    'cursor-not-allowed': disabled,
    'cursor-pointer': !disabled,
  });

  const switchThumb = classNames('rounded-full w-4 h-4 absolute transition-all', {
    [`${bgColor} right-0.5 top-0.5`]: checked, // dynamic background color for the thumb
    'bg-gray-400 left-0.5 top-0.5': !checked, // default when unchecked
  });

  return (
    <div className={switchRoot}>
      <input
        className={inputStyles}
        type='checkbox'
        disabled={disabled}
        onChange={(e) => {
          setChecked(e.currentTarget.checked);
          if (onChange) {
            onChange(e);
          }
        }}
        onClick={(e) => {
          // onClick Event will prevent Changing Checked State
          e.currentTarget.checked = !e.currentTarget.checked;
          if (onClick) {
            onClick(e);
          }
        }}
        ref={ref}
        checked={checked}
      />
      <span className={switchThumb} />
    </div>
  );
});

Switch.displayName = 'Switch';

export default Switch;
